import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { giftUpdates } from '@theme/camixa-gift-rules';

const element = document.getElementById('camixa-gift-config');
if (element) {
  const config = JSON.parse(element.textContent);
  const root = window.Shopify?.routes?.root || '/';
  const pending = new Set();
  const ownEvents = new WeakSet();
  let running;
  let revision = 0;
  let quantityTimer;
  let checkoutPending = false;
  let allowSubmit = false;
  let lastMessage = '';
  let selection;

  function selectorButtons() {
    document.querySelectorAll('[data-gift-select-submit]').forEach((button) => {
      button.disabled = Boolean(selection);
    });
  }

  function sections() {
    return [...new Set([...document.querySelectorAll('cart-items-component')]
      .map((node) => node.dataset.sectionId).filter(Boolean))].slice(0, 5);
  }

  function publish(cart) {
    const deferred = CartLinesUpdateEvent.createPromise();
    const event = new CartLinesUpdateEvent({ action: 'update', context: 'cart', promise: deferred.promise });
    ownEvents.add(event);
    document.dispatchEvent(event);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { sections: cart.sections, items: cart.items, itemCount: cart.item_count },
    });
  }

  function message(text) {
    lastMessage = text;
    document.querySelectorAll('[data-gift-guard-message]').forEach((node) => {
      node.textContent = text;
      node.hidden = !text;
    });
  }

  async function request(path, body, expectItems = true) {
    const response = await fetch(root + path, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...(body ? { body: JSON.stringify(body) } : {}),
      signal: AbortSignal.timeout(15000),
    });
    const cart = await response.json();
    if (!response.ok || cart.errors || (expectItems && !Array.isArray(cart.items))) throw new Error('Cart unavailable');
    return cart;
  }

  async function clean() {
    document.documentElement.dataset.giftChecking = 'true';
    try {
      for (let attempt = 0; attempt < 5; attempt++) {
        while (pending.size) await Promise.allSettled([...pending]);
        const version = revision;
        const cart = await request('cart.js');
        if (version !== revision || pending.size) continue;
        const result = giftUpdates(cart, config);
        document.documentElement.dataset.cartHasGift = String(result.hasGift);
        if (!Object.keys(result.updates).length) {
          message(lastMessage === config.error ? '' : lastMessage);
          return cart;
        }
        const deferred = CartLinesUpdateEvent.createPromise();
        const event = new CartLinesUpdateEvent({ action: 'remove', context: 'cart', promise: deferred.promise });
        ownEvents.add(event);
        document.dispatchEvent(event);
        try {
          const updated = await request('cart/update.js', {
            updates: result.updates, sections: sections(), sections_url: location.pathname,
          });
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updated),
            detail: { sections: updated.sections, items: updated.items, itemCount: updated.item_count },
          });
          message(config.removed);
        } catch (error) {
          deferred.reject(error);
          throw error;
        }
      }
      throw new Error('Cart kept changing');
    } finally {
      delete document.documentElement.dataset.giftChecking;
    }
  }

  function check() {
    if (!running) running = clean().finally(() => { running = null; });
    return running;
  }
  const schedule = () => { if (!selection) check().catch(() => message(config.error)); };

  async function chooseGift(variantId) {
    document.documentElement.dataset.giftSelecting = 'true';
    try {
      await new Promise((resolve) => setTimeout(resolve, 400));
      let cart = await check();
      if (!giftUpdates(cart, config).eligible) {
        message(config.ineligible);
        return;
      }
      const product = await request(`products/${encodeURIComponent(config.productHandle)}.js`, null, false);
      const variant = product.variants?.find((item) => String(item.id) === variantId);
      if (String(product.id) !== String(config.productId) || !variant?.available || variant.price !== 0) {
        throw new Error('Gift unavailable');
      }
      // Recheck spend after the product request, before changing the cart.
      cart = await check();
      if (!giftUpdates(cart, config).eligible) {
        message(config.ineligible);
        return;
      }
      const existing = cart.items.find((item) => String(item.product_id) === String(config.productId) && String(item.variant_id) === variantId);
      if (!existing) await request('cart/add.js', { items: [{ id: Number(variantId), quantity: 1 }] });
      cart = await request('cart.js');
      const updates = {};
      let kept = false;
      for (const item of cart.items) {
        if (String(item.product_id) !== String(config.productId)) continue;
        const keep = !kept && String(item.variant_id) === variantId && item.original_price === 0;
        const quantity = keep ? 1 : 0;
        if (keep) kept = true;
        if (item.quantity !== quantity) updates[item.key] = quantity;
      }
      const updated = await request('cart/update.js', { updates, sections: sections(), sections_url: location.pathname });
      publish(updated);
      const finalCart = await check();
      const selected = finalCart.items.some((item) => String(item.product_id) === String(config.productId) && String(item.variant_id) === variantId && item.original_price === 0);
      message(selected ? config.added : config.ineligible);
    } catch {
      // If the add succeeded but a later request failed, reconcile the cart again.
      try { await check(); } catch { /* The retry message keeps checkout guarded. */ }
      message(config.addError);
    } finally {
      delete document.documentElement.dataset.giftSelecting;
    }
  }

  for (const name of [StandardEvents.cartLinesUpdate, StandardEvents.cartDiscountUpdate]) {
    document.addEventListener(name, (event) => {
      if (ownEvents.has(event)) return;
      revision++;
      const settled = Promise.resolve(event.promise).catch(() => {}).finally(() => {
        pending.delete(settled);
        schedule();
      });
      pending.add(settled);
    });
  }
  document.addEventListener('quantity-selector:update', () => {
    revision++;
    clearTimeout(quantityTimer);
    quantityTimer = setTimeout(schedule, 400);
  });

  async function checkout(form, submitter) {
    if (checkoutPending) return;
    checkoutPending = true;
    try {
      await new Promise((resolve) => setTimeout(resolve, 400));
      if (selection) await selection;
      await check();
      if (form.isConnected) {
        allowSubmit = true;
        try { form.requestSubmit(submitter?.isConnected ? submitter : document.querySelector('[name="checkout"]')); }
        finally { allowSubmit = false; }
      } else {
        const currentForm = document.getElementById('cart-form');
        const currentButton = document.querySelector('[name="checkout"]');
        if (!currentForm || !currentButton || currentButton.disabled) return;
        allowSubmit = true;
        try { currentForm.requestSubmit(currentButton); } finally { allowSubmit = false; }
      }
    } catch {
      message(config.error);
    } finally {
      checkoutPending = false;
    }
  }
  document.addEventListener('submit', (event) => {
    if (event.target.matches?.('[data-gift-selector]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (selection || checkoutPending) return;
      const variantId = event.target.querySelector('[name="gift_variant"]')?.value;
      if (!variantId) return;
      selection = chooseGift(variantId).finally(() => {
        selection = null;
        selectorButtons();
        schedule();
      });
      selectorButtons();
      return;
    }
    if (allowSubmit || event.target.id !== 'cart-form') return;
    if (event.submitter && event.submitter.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkout(event.target, event.submitter);
  }, true);
  document.addEventListener('click', (event) => {
    if (document.documentElement.dataset.giftChecking !== 'true' && !selection) return;
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('cart-items-component button, cart-items-component input, cart-items-component select, .cart__ctas')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
  new MutationObserver(() => {
    selectorButtons();
    document.querySelectorAll('[data-gift-guard-message]').forEach((node) => {
      if (node.textContent !== lastMessage) node.textContent = lastMessage;
      node.hidden = !lastMessage;
    });
  }).observe(document.body, { childList: true, subtree: true });
  window.addEventListener('pageshow', (event) => { if (event.persisted) schedule(); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) schedule(); });
  if (config.hasGift) schedule();
  selectorButtons();
}
